import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { FileUploadField } from './file-upload/FileUploadField';
import { createBlobUrl } from '@/utils/files/conversion';

interface PersonalInfoFormData {
  fullName: string;
  description: string;
  photoUrl: string;
  resumeUrl: string;
  isResumePublic: boolean;
  forSelf: boolean;
}

interface PersonalInfoFormProps {
  formData: PersonalInfoFormData;
  onChange: (data: PersonalInfoFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel?: string;
}

export function PersonalInfoForm({
  formData,
  onChange,
  onSubmit,
  submitLabel = 'حفظ',
}: PersonalInfoFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<{photo?: string; resume?: string}>({});

  // Create preview URLs when files change
  useEffect(() => {
    if (formData.photoUrl) {
      const photoUrl = createBlobUrl(formData.photoUrl);
      setPreviewUrls(prev => ({ ...prev, photo: photoUrl }));
    }
    if (formData.resumeUrl) {
      const resumeUrl = createBlobUrl(formData.resumeUrl);
      setPreviewUrls(prev => ({ ...prev, resume: resumeUrl }));
    }

    // Cleanup URLs on unmount or when files change
    return () => {
      Object.values(previewUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [formData.photoUrl, formData.resumeUrl]);

  const handleChange = (field: keyof PersonalInfoFormData, value: any) => {
    setError(null);
    onChange({ ...formData, [field]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName || !formData.description) {
      setError('يرجى تعبئة الحقول المطلوبة');
      return;
    }
    onSubmit(e);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="fullName">الاسم الرباعي</Label>
        <Input
          id="fullName"
          required
          value={formData.fullName}
          onChange={(e) => handleChange('fullName', e.target.value)}
          placeholder="أدخل الاسم الرباعي"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">نبذة مختصرة</Label>
        <Textarea
          id="description"
          required
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="اكتب نبذة مختصرة"
        />
      </div>

      <div className="space-y-2">
        <Label>الصورة الشخصية (اختياري)</Label>
        <FileUploadField
          type="image"
          value={formData.photoUrl}
          onChange={(value) => handleChange('photoUrl', value)}
          label="الصورة الشخصية"
          onError={setError}
          previewUrl={previewUrls.photo}
        />
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>السيرة الذاتية PDF (اختياري)</Label>
          <FileUploadField
            type="pdf"
            value={formData.resumeUrl}
            onChange={(value) => handleChange('resumeUrl', value)}
            label="ملف السيرة الذاتية"
            onError={setError}
            previewUrl={previewUrls.resume}
          />
        </div>

        {formData.resumeUrl && (
          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
            <Switch
              checked={formData.isResumePublic}
              onCheckedChange={(checked) => handleChange('isResumePublic', checked)}
            />
            <Label className="text-sm text-gray-700">
              جعل السيرة الذاتية متاحة للجميع
            </Label>
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}

      <Button
        type="submit"
        className="w-full hover:bg-secondary"
        disabled={!formData.fullName || !formData.description}
      >
        {submitLabel}
      </Button>
    </form>
  );
}